import type { GroupedCodingResult } from 'src/types/dashboard';
import type {
  SentimentFilter,
  Sorting,
  TopicFilter,
} from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/types';

export const filterTopic = (
  groupedCodingResults: GroupedCodingResult[],
  topicFilter: TopicFilter,
) => {
  if (topicFilter === 'subtopics') {
    return groupedCodingResults.reduce(
      (acc: GroupedCodingResult[], { subTopics }) =>
        subTopics ? [...acc, ...subTopics] : acc,
      [],
    );
  }
  return groupedCodingResults;
};

export const filterSentimental = (
  groupedCodingResults: GroupedCodingResult[],
  sentimentFilter: SentimentFilter,
) => {
  const result: GroupedCodingResult[] = groupedCodingResults.map((gcr) => ({
    ...gcr,
    subTopics: gcr.subTopics
      ? filterSentimental(gcr.subTopics, sentimentFilter)
      : undefined,
  }));
  switch (sentimentFilter) {
    case 'positiveCount':
      return result.filter(({ positiveCount }) => !!positiveCount);
    case 'negativeCount':
      return result.filter(({ negativeCount }) => !!negativeCount);
    default:
      return result;
  }
};

const relevanceMap: {
  [key in SentimentFilter]: keyof Pick<
    GroupedCodingResult,
    | 'relevanceAll'
    | 'relevanceNegative'
    | 'relevanceNeutral'
    | 'relevancePositive'
  >;
} = {
  totalOfAllCounts: 'relevanceAll',
  negativeCount: 'relevanceNegative',
  neutralCount: 'relevanceNeutral',
  positiveCount: 'relevancePositive',
};

export const applySorting = (
  groupedCodingResults: GroupedCodingResult[],
  sorting: Sorting,
  sentimentFilter: SentimentFilter,
  cutoff: number,
) => {
  groupedCodingResults.map(
    ({ subTopics }) =>
      subTopics && applySorting(subTopics, sorting, sentimentFilter, cutoff),
  );
  switch (sorting) {
    case 'frequency':
      return groupedCodingResults.sort(
        ({ [sentimentFilter]: totalA }, { [sentimentFilter]: totalB }) =>
          totalB - totalA,
      );
    case 'relevance': {
      const key = relevanceMap[sentimentFilter];
      return (
        groupedCodingResults
          .sort(
            ({ [key]: relevanceA }, { [key]: relevanceB }) =>
              relevanceB - relevanceA,
          )
          // Slice the sorted array to limit to the first 5 topics only
          .slice(0, cutoff)
          .map((category) => ({
            ...category,
            relevanceRating: category[key],
          }))
      );
    }
    default:
      return groupedCodingResults;
  }
};

export const generateChatBotTexts = (
  categories: GroupedCodingResult[],
  maxAmount = 3,
) => {
  let primeText = '';
  categories
    .sort(
      ({ negativeCount: negativeCountA }, { negativeCount: negativeCountB }) =>
        negativeCountB - negativeCountA,
    )
    .slice(0, maxAmount)
    .forEach(({ label }, i, items) => {
      if (i + 1 === maxAmount || i + 1 === items.length) {
        primeText = `${primeText} and ${label.toLowerCase()}`;
        return;
      }
      primeText = primeText
        ? `${primeText}, ${label.toLowerCase()}`
        : label.toLowerCase();
    });
  return primeText;
};
